import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCcw, Save } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { type RiskConfig, riskService } from '@/services/risk';

export function RiskConfigForm() {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Partial<RiskConfig>>({});

  const { data: config, isLoading } = useQuery({
    queryKey: ['risk', 'config'],
    queryFn: () => riskService.getConfig(),
  });

  useEffect(() => {
    if (config) {
      setFormData(config);
    }
  }, [config]);

  const mutation = useMutation({
    mutationFn: (config: Partial<RiskConfig>) => riskService.updateConfig(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risk'] });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  const handleReset = () => {
    if (config) setFormData(config);
  };

  if (isLoading) {
    return <div className="animate-pulse h-96 bg-muted rounded" />;
  }

  const fields = [
    { key: 'totalBudget', label: 'Total Budget', description: 'Maximum amount to invest' },
    {
      key: 'maxPositionSize',
      label: 'Max Position Size',
      description: 'Maximum value per position',
    },
    { key: 'maxPerTrade', label: 'Max Per Trade', description: 'Maximum value per trade' },
    {
      key: 'dailyLossLimit',
      label: 'Daily Loss Limit',
      description: 'Stop trading after this loss',
    },
    {
      key: 'maxPositions',
      label: 'Max Positions',
      description: 'Maximum number of positions',
      isInt: true,
    },
    {
      key: 'maxConcentrationPct',
      label: 'Max Concentration %',
      description: 'Max % in any single stock',
    },
    {
      key: 'requireConfirmationAbove',
      label: 'Confirm Above',
      description: 'Require confirmation for orders above this',
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Risk Limits</CardTitle>
        <CardDescription>Configure your trading risk parameters</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {fields.map(({ key, label, description, isInt }) => (
            <div key={key}>
              <label className="text-sm font-medium">{label}</label>
              <p className="text-xs text-muted-foreground mb-1">{description}</p>
              <Input
                type="number"
                step={isInt ? '1' : '0.01'}
                value={
                  formData[key as keyof RiskConfig] != null
                    ? String(formData[key as keyof RiskConfig])
                    : ''
                }
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    [key]:
                      value === '' ? undefined : isInt ? parseInt(value, 10) : parseFloat(value),
                  }));
                }}
              />
            </div>
          ))}

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive ?? true}
              onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
              className="h-4 w-4 rounded border-gray-300"
            />
            <label htmlFor="isActive" className="text-sm">
              Risk limits active
            </label>
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="submit" disabled={mutation.isPending}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
            <Button type="button" variant="outline" onClick={handleReset}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          </div>

          {mutation.isSuccess && (
            <p className="text-green-500 text-sm">Configuration saved successfully!</p>
          )}
          {mutation.isError && <p className="text-red-500 text-sm">Failed to save configuration</p>}
        </form>
      </CardContent>
    </Card>
  );
}

export default RiskConfigForm;
